import React from 'react';
import classNameBind from 'classnames/bind';
import round from 'lodash/round';
import { withTranslation } from '../Translator';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import Text from '../Text';
import StackGrid from '../StackGrid';
import generateComparisonColor from '../../utils/methods/generateComparisonColor';
import styles from './MetricTotals.module.css';
import generateLineColors from '../../utils/methods/generateLineColors';

const bindClasses = classNameBind.bind(styles);

const MetricTotals = ({
  items,
  onClick,
  segments,
  ranges,
  notSelectable,
  condensed,
  withTooltips,
  t,
} = {}) => {
  const lineColors = generateLineColors(items.length);
  const renderDiff = (diff, reverseComparisonColor) => (
    <div
      className={bindClasses('diff', generateComparisonColor(diff, reverseComparisonColor))}
      data-ppuc-id="MetricsTotal.diff"
    >
      {diff !== 0 && (
        <Icon
          name={`trend-${diff > 0 ? 'up' : 'down'}`}
          className={bindClasses('icon')}
          color={generateComparisonColor(diff, reverseComparisonColor)}
        />
      )}
      {`${t('number', { number: round(diff * 100, 2) })}%`}
    </div>
  );

  const renderSegmentSection = (segment, value, caption, isLeft, diff, reverseComparisonColor) => (
    <div
      className={bindClasses(`segments-${isLeft ? 'left' : 'right'}`)}
      data-ppuc-id="MetricsTotal.segment"
    >
      <div
        className={bindClasses('segment-name', isLeft ? 'violet' : 'teal')}
        data-ppuc-id="MetricsTotal.segment.name"
      >
        <span>{segment}</span>
      </div>
      <Tooltip content={withTooltips && value}>
        <div
          className={bindClasses('value')}
          data-ppuc-id="MetricsTotal.segment.value"
        >
          {value}
        </div>
      </Tooltip>
      {caption && <Text type="subdued">{caption}</Text>}
      {(diff || diff === 0) ? renderDiff(diff, reverseComparisonColor) : ''}
    </div>
  );
  const renderItem = (el, index, activeIndex) => (
    <div
      className={bindClasses('element', notSelectable || el.notSelectable ? 'notSelectable' : '')}
      onClick={() => { onClick(el, index); }}
      role="button"
      tabIndex={index}
      key={el.id ? el.id : index}
      data-ppuc-id="MetricsTotal.item"
    >
      <div
        className={bindClasses('name')}
        data-ppuc-id="MetricsTotal.item.name"
      >
        {el.name}
      </div>
      {ranges && ranges.length === 2
        ? (
          <div
            className={bindClasses('ranges')}
            data-ppuc-id="MetricsTotal.item.range"
          >
            {ranges[0]}
          </div>
        )
        : ''}
      {segments && segments.length === 2 && el.values.length >= 2
        ? (
          <div className={bindClasses('segments-wrapper')}>
            {renderSegmentSection(segments[0], el.values[0], el.captions[0], true, el.diffs && el.diffs.length === 2 ? el.diffs[0] : '', el.reverseComparisonColor)}
            {renderSegmentSection(segments[1], el.values[1], el.captions[1], false, el.diffs && el.diffs.length === 2 ? el.diffs[1] : '', el.reverseComparisonColor)}
          </div>
        )
        : (
          <React.Fragment>
            <Tooltip content={withTooltips && el.values[0]}>
              <div
                className={bindClasses('value')}
                data-ppuc-id="MetricsTotal.segment.value"
              >
                {el.values[0]}
                {el.diffs && el.diffs.length > 0 && (el.diffs[0] || el.diffs[0] === 0) ? renderDiff(el.diffs[0], el.reverseComparisonColor) : ''}
              </div>
            </Tooltip>
            {el.captions[0] && <Text type="subdued">{el.captions[0]}</Text>}
          </React.Fragment>
        )}
      {ranges && ranges.length === 2 && el.values.length >= 2
        ? (
          <React.Fragment>
            <div
              className={bindClasses('ranges')}
              data-ppuc-id="MetricsTotal.item.range"
            >
              {ranges[1]}
            </div>
            {segments && segments.length === 2 && el.values.length === 4
              ? (
                <div className={bindClasses('segments-wrapper')}>
                  {renderSegmentSection(segments[0], el.values[2], el.captions[2], true)}
                  {renderSegmentSection(segments[1], el.values[3], el.captions[3])}
                </div>
              )
              : (
                <React.Fragment>
                  <Tooltip content={withTooltips && el.values[1]}>
                    <div
                      className={bindClasses('value')}
                      data-ppuc-id="MetricsTotal.segment.value"
                    >
                      {el.values[1]}
                    </div>
                  </Tooltip>
                  {el.captions[1] && <Text type="subdued">{el.captions[1]}</Text>}
                </React.Fragment>
              )}
          </React.Fragment>
        )
        : ''}
      {el.selected
        ? (
          <div
            className={bindClasses('selected')}
            data-ppuc-id="MetricsTotal.item.line.selected"
            style={{
              backgroundColor: el.color
                ? lineColors.filter(item => item.name === el.color)[0].value
                : lineColors[activeIndex % lineColors.length].value,
            }}
          />
        )
        : (
          <div
            className={bindClasses('notSelected', notSelectable || el.notSelectable ? 'notSelectable' : '')}
            data-ppuc-id="MetricsTotal.item.line.notSelected"
          />
        )}
    </div>
  );

  let activeIndex = 0;

  return (
    <StackGrid
      dataId="MetricTotals"
      spacingHorizontal="wide"
      // eslint-disable-next-line no-nested-ternary
      itemMinWidth={segments && segments.length === 2 ? 320 : condensed ? 180 : 230}
      itemMinHeight={0}
    >
      {
        items.map((el, index) => {
          const element = {
            ...el,
            captions: el.captions || ['', '', '', ''],
          };

          if (el.selected) {
            activeIndex += 1;

            return renderItem(element, index, activeIndex - 1);
          }

          return renderItem(element, index, activeIndex);
        })
      }
    </StackGrid>
  );
};

MetricTotals.defaultProps = {
  onClick: () => {},
  items: [],
  segments: [],
  ranges: [],
  notSelectable: false,
  condensed: false,
};

const enhanced = withTranslation('components')(MetricTotals);
enhanced.displayName = 'MetricTotals';

export default enhanced;
export const PureComponent = MetricTotals;
